import {connect} from 'pwa-helpers';
import {LitElement, html} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {msg, str} from '@lit/localize';
import {BILLING_ANNUALLY, BILLING_MONTHLY} from '../../services/subscription/billingCycle';
import {SUB_ACTIVE, SUB_TRIALING} from '../../services/subscription/subscriptionStatus';
import {openDialog} from '../../slices/statusSlice';
import {updateSubscription} from '../../slices/accountSlice';
import paddle from '../../services/subscription/paddleService';
import {
	selectLanguage,
	selectPlans,
	selectSubscribedPlan,
	selectSubscribedPrice,
	selectPrices,
	selectCurrentBillingCycle,
	selectCurrentSubscription,
	selectIsTrialing,
} from '../../selectors';
import store from '../../store';
import style from '../../../../scss/choosePlan.scss';
import {logSubscriptionEvent, changeSubscription} from '../../slices/planSlice';
import {getValueForLanguage} from '../../util/getValueForLanguage';

@customElement('arc-choose-plan')
export class ChoosePlan extends connect(store)(LitElement) {
	static styles = [style];

	static properties = {
		plans: {type: Array},
		subscribedPlan: {type: Object},
		subscribedPrice: {type: Object},
		lang: {type: String},
		accountData: {type: Object},
		selectedBillingCycle: {type: String},
		prices: {type: Array},
		dashboardLink: {type: String},
		isTrialing: {type: Boolean},
	};

	constructor() {
		super();
		this.plans = [];
		this.subscription = null;
		this.subscribedPlan = null;
		this.subscribedPrice = null;
		this.lang = 'en';
		this.accountData = null;
		this.selectedBillingCycle = null;
		this.selectedPlan = null;
		this.dashboardLink = null;
		this.allowDowngrades = false;
		this.isTrialing = false;
		this.prices = [];
	}

	stateChanged(state) {
		this.plans = selectPlans(state);
		this.subscription = selectCurrentSubscription(state);
		this.subscribedPlan = selectSubscribedPlan(state);
		this.subscribedPrice = selectSubscribedPrice(state);
		this.isTrialing = selectIsTrialing(state);
		this.prices = selectPrices(state);
		this.lang = selectLanguage(state);
		this.selectedBillingCycle = this.selectedBillingCycle ?? selectCurrentBillingCycle(state);
		this.accountData = state.account.data.account;
		this.dashboardLink = state.plan.dashboardLink;
	}

	onCheckoutEvent(ev) {
		if (ev.name === 'checkout.completed' && this.selectedPlan) {
			// put provisional subscription data in store
			const trialPeriod = ev.data.items[0].trial_period;
			const status = trialPeriod ? SUB_TRIALING : SUB_ACTIVE;
			let trialEndsAt = null;
			if (trialPeriod) {
				const end = new Date();
				end.setDate(end.getDate() + trialPeriod.frequency);
				trialEndsAt = `${end.getFullYear()}-${
					end.getMonth() + 1
				}-${end.getDate()} ${end.getHours()}:${end.getMinutes()}:${end.getSeconds()}`;
			}
			store.dispatch(
				updateSubscription({
					trialEndsAt: trialEndsAt,
					status: status,
					plan: this.selectedPlan,
					price: this.selectedPlan.prices.find((p) => p.billingCycle === this.selectedBillingCycle),
				})
			);
		}
	}

	async connectedCallback() {
		super.connectedCallback();
		this.allowDowngrades = store.getState().status.allowDowngrades;
		await paddle.initialize(
			store.getState().status.paddleToken,
			this.lang,
			this.onCheckoutEvent.bind(this)
		);
	}

	selectPlan(plan, alternateBillingCycle) {
		this.selectedPlan = plan;
		const price = plan.prices.find((p) => p.billingCycle === this.selectedBillingCycle);
		if (this.subscribedPrice) {
			// change existing subscription
			// we could be upgrading, changing billing cycle, or, possibly downgrading
			const billingCycleLabel =
				this.selectedBillingCycle === BILLING_MONTHLY ? msg('Monthly') : msg('Annual');
			const dialogOptions = {
				title: msg('Upgrade'),
				message: msg(
					str`You are about to change your subscription to ${getValueForLanguage(
						plan.longName,
						this.lang
					)}. The new ${billingCycleLabel.toLowerCase()} price will be ${price.price / 100}€.`
				),
				labelConfirm: msg('OK'),
				labelCancel: msg('Cancel'),
			};
			if (alternateBillingCycle) {
				dialogOptions.title = msg('Change Billing Cycle');
				dialogOptions.message = msg(
					str`You are about to change the billing cycle for your subscription. The new ${billingCycleLabel.toLowerCase()} price will be ${
						price.price / 100
					}€.`
				);
			}
			const handleConfirm = async () => {
				store.dispatch(changeSubscription({priceId: price.id}));
				window.removeEventListener('confirm', handleConfirm);
			};
			window.addEventListener('confirm', handleConfirm);
			store.dispatch(openDialog(dialogOptions));
		} else {
			// open checkout for new subscription
			paddle.openCheckout(price.providerId, this.accountData.email, {
				aaki_account_id: this.accountData.id,
				aaki_plan_id: plan.id,
				aaki_price_id: price.id,
			});
		}
	}

	render() {
		const billingCycleOptions = [
			{value: BILLING_MONTHLY, label: msg('Monthly')},
			{value: BILLING_ANNUALLY, label: msg('Annual')},
		];
		return html`
			<section class="choosePlanChoice">
				<div class="choosePlanBillingCycle">
					<arc-switch
						title="${msg('Billing Cycle')}"
						.tooltip=${this.isTrialing ? null : msg('Choose your billing cycle.')}
						.options=${billingCycleOptions}
						.value=${this.selectedBillingCycle}
						?disabled="${this.isTrialing}"
						@switch-change=${(e) => {
							if (!this.isTrialing) {
								this.selectedBillingCycle = e.detail.value;
							}
						}}
					></arc-switch>
				</div>
				<div class="choosePlanCards">
					${this.plans.map((plan) => {
						let price = plan.prices.find((p) => p.billingCycle === this.selectedBillingCycle);
						if (!price) {
							price = {price: 0};
						}
						const isCurrentCycle = this.selectedBillingCycle === this.subscribedPrice?.billingCycle;
						if (this.selectedBillingCycle === BILLING_ANNUALLY) {
							price = {price: price.price / 12}; // show annual prices as monthly prices
						}
						const alternateBillingCycle = plan.isCurrent && !isCurrentCycle;
						// you can’t change a plan during the trial period.
						const canChooseThis = (plan.canChoose || alternateBillingCycle) && !this.isTrialing;
						let actionLabel = msg('Select Plan');
						if (plan.isUpgrade) {
							actionLabel = msg('Upgrade Plan');
						}
						if (plan.isDowngrade) {
							actionLabel = msg('Downgrade Plan');
						}
						if (alternateBillingCycle) {
							actionLabel = msg('Change Billing');
						}
						return html`
							<div class="choosePlanCard ${plan.isCurrent ? 'current' : ''}">
								<h3 class="choosePlanName">${getValueForLanguage(plan.name, this.lang)}</h3>
								<div class="choosePlanPrice">${plan.priceOnRequest? (html`<a href="">${msg('Price on Request')}</a>`) :
									(price.price / 100) + ' €'}</div>
								<div class="choosePlanDescription">
									${getValueForLanguage(plan.description, this.lang)}
								</div>
								${canChooseThis
									? html`
											<arc-button
												@click=${() => this.selectPlan(plan, alternateBillingCycle)}
												title="${actionLabel}"
												type="primary"
												.additionalInfo="${false}"
											>
											</arc-button>
									  `
									: null}
							</div>
						`;
					})}
				</div>
			</section>
		`;
	}
}
